import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dispatch, SetStateAction, useState, useEffect, JSX } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

const COOKIE_NAME = "hide_experimental_feature_modal_";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  actionFn: () => void;
  featureID: string;
};

/**
 * ExperimentalFeatureModal component displays a modal dialog for experimental features.
 * It allows users to opt-out of seeing the modal again by setting a cookie.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.open - Determines if the modal is open.
 * @param {function} props.onOpenChange - Callback function to handle the modal open state change.
 * @param {function} props.actionFn - Function to be executed when the action button is clicked.
 *
 * @returns {JSX.Element} The rendered ExperimentalFeatureModal component.
 *
 * @component
 * @example
 * <ExperimentalFeatureModal
 *   open={true}
 *   onOpenChange={(isOpen) => console.log(isOpen)}
 *   actionFn={() => console.log('Action executed')}
 * />
 */
const ExperimentalFeatureModal = ({ open, onOpenChange, actionFn, featureID }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["modals", "common"]);
  const [doNotShow, setDoNotShow] = useState(false);

  // Check cookie on mount
  useEffect(() => {
    const hideModal = Cookies.get(COOKIE_NAME+featureID);
    if (hideModal === "true") {
      onOpenChange(false);
    }
  }, [onOpenChange]);

  const handleAction = () => {
    if (doNotShow) {
      // Set cookie to expire in 30 days
      Cookies.set(COOKIE_NAME+featureID, "true", { expires: 30 });
    }
    actionFn();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="">
        <DialogHeader>
          <DialogTitle className="flex text-red-500 mb-2">
            ⚠️ {t("experimentalfeature.title")}
          </DialogTitle>
        </DialogHeader>
        <>
          <p className="">
            {t("experimentalfeature.description")}
          </p>
          <p className="">
            {t("experimentalfeature.footnote")}
          </p>
          <div className="flex items-center space-x-2 mt-2">
            <Checkbox
              id="doNotShow"
              checked={doNotShow}
              onCheckedChange={(checked) => setDoNotShow(checked as boolean)}
            />
            <label
              htmlFor="doNotShow"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("common:donotshowagain")}
            </label>
          </div>
        </>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">
              {t("common:buttons.cancel")}
            </Button>
          </DialogClose>
          <Button onClick={handleAction}>
            {t("common:buttons.continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExperimentalFeatureModal;
